/*
 * scroll-rescue.js — red de seguridad GLOBAL contra "scroll trabado".
 *
 * Problema que resuelve: varios dueños distintos bloquean el scroll del sitio
 * (menu drawer del theme via body.disable-scroll, popups de Klaviyo via
 * body.klaviyo-prevent-body-scrolling, lightbox de reseñas via body.style.overflow,
 * modales m3x1/gus/mgel via clases en <html>, carrito Rebuy, chat Chatwoot).
 * Si CUALQUIERA de ellos no limpia su lock al cerrar, el usuario queda con la
 * pagina viendose normal pero sin poder scrollear -> mata el CVR.
 *
 * Esto NO reemplaza a nadie: solo revisa periodicamente y, si NO hay ningun
 * modal realmente visible, deshace los locks huerfanos. Si hay un modal abierto,
 * no toca nada.
 *
 * La logica de decision es una funcion PURA (`decide`) para poder testearla en
 * Node sin navegador: scripts/test-scroll-rescue.mjs
 */
(function (global) {
  'use strict';

  // Un <details> del menu que dice estar abierto pero nunca se muestra deja
  // #MainContent en opacity:0 (pantalla blanca) + body bloqueado. Le damos
  // margen de sobra para que su animacion (~100ms) termine antes de rescatar.
  var GRACE_MS = 2500;

  // Neutralizar overlays invisibles queda APAGADO por default: un overlay
  // transparente a pantalla completa TAMBIEN puede ser un "click-catcher"
  // legitimo de alguna app (cerrar dropdown al tocar afuera), y matarle los
  // taps romperia esa funcion. Limpiar locks huerfanos, en cambio, es seguro
  // por definicion. Se puede prender si algun dia se confirma un overlay zombie.
  var NEUTRALIZE_ORPHAN_OVERLAYS = false;

  var BODY_LOCK_CLASSES = [
    'disable-scroll',
    'overflow-hidden-tablet',
    'overflow-hidden-mobile',
    'overflow-hidden',
    'klaviyo-prevent-body-scrolling',
    'js-video-modal-disable-scroll',
    'popup-currency-selector-is-open'
  ];
  var HTML_LOCK_CLASSES = ['m3x1-zoom-lock', 'gus-scroll-lock', 'mgel-modal-lock'];

  /**
   * Decide que rescatar. Pura: recibe un snapshot, devuelve acciones.
   * @param {{bodyLockClasses:string[], htmlLockClasses:string[], bodyInlineOverflow:string,
   *          bodyInlinePosition:string, visibleBlockers:string[], detailsOpen:boolean,
   *          detailsMenuOpening:boolean, detailsOpenForMs:number,
   *          orphanOverlays:number[]}} s
   */
  function decide(s, opts) {
    var allowNeutralize = opts && typeof opts.neutralizeOverlays === 'boolean'
      ? opts.neutralizeOverlays : NEUTRALIZE_ORPHAN_OVERLAYS;
    var out = { clearBodyLocks: false, clearHtmlLocks: false, clearInlineStyles: false,
                closeDetails: false, neutralize: [], reasons: [] };

    var blockers = s.visibleBlockers || [];
    // Un menu que esta abierto Y mostrandose cuenta como blocker legitimo,
    // incluso durante su transicion de apertura.
    var menuLegit = !!(s.detailsOpen && s.detailsMenuOpening);
    var somethingOpen = blockers.length > 0 || menuLegit;

    // 1) Estado roto del menu: dice [open] pero nunca aparecio -> pantalla blanca.
    if (s.detailsOpen && !s.detailsMenuOpening && s.detailsOpenForMs > GRACE_MS) {
      out.closeDetails = true;
      out.reasons.push('menu-details-open-sin-menu-opening');
    }

    if (somethingOpen && !out.closeDetails) return out; // hay algo abierto de verdad: no tocar

    // 2) Locks huerfanos (nadie visible los justifica).
    if ((s.bodyLockClasses || []).length) { out.clearBodyLocks = true; out.reasons.push('body-lock-classes-huerfanas'); }
    if ((s.htmlLockClasses || []).length) { out.clearHtmlLocks = true; out.reasons.push('html-lock-classes-huerfanas'); }
    if (s.bodyInlineOverflow === 'hidden' || s.bodyInlinePosition === 'fixed') {
      out.clearInlineStyles = true; out.reasons.push('body-inline-lock-huerfano');
    }

    // 3) Overlays invisibles que se comen los taps (el clasico "se ve bien pero nada responde").
    if (allowNeutralize && (s.orphanOverlays || []).length) {
      out.neutralize = s.orphanOverlays.slice();
      out.reasons.push('overlay-invisible-intercepta-taps');
    }
    return out;
  }

  // ---- Node (tests) ----
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { decide: decide, GRACE_MS: GRACE_MS,
                       NEUTRALIZE_ORPHAN_OVERLAYS: NEUTRALIZE_ORPHAN_OVERLAYS,
                       BODY_LOCK_CLASSES: BODY_LOCK_CLASSES, HTML_LOCK_CLASSES: HTML_LOCK_CLASSES };
    return;
  }

  // ---- Navegador ----
  var doc = global.document;
  if (!doc) return;

  var detailsOpenSince = 0;

  function isVisible(el) {
    if (!el) return false;
    if (el.hidden) return false;
    var cs = global.getComputedStyle(el);
    if (cs.display === 'none' || cs.visibility === 'hidden' || parseFloat(cs.opacity) === 0) return false;
    var r = el.getBoundingClientRect();
    return r.width > 1 && r.height > 1;
  }

  // Modales "propios" + de terceros que SI son blockers legitimos cuando se ven.
  var BLOCKER_SELECTORS = [
    '.menu-drawer', '.rebuy-cart__flyout', '.klaviyo-form',
    '.m3x1-carousel__zoom-modal', '.m3x1-garantia', '.m3x1-color-popup',
    '.gus__viewer.is-open', '.mgel-modal', '[data-skr-lb].skr-on',
    'dialog[open]', '[aria-modal="true"]'
  ];

  function collect() {
    var body = doc.body, html = doc.documentElement;
    var det = doc.querySelector('details.menu-drawer-container');
    var detOpen = !!(det && det.open);
    var menuOpening = !!(det && det.classList.contains('menu-opening'));

    if (detOpen) { if (!detailsOpenSince) detailsOpenSince = Date.now(); }
    else { detailsOpenSince = 0; }

    var blockers = [];
    for (var i = 0; i < BLOCKER_SELECTORS.length; i++) {
      var nodes = doc.querySelectorAll(BLOCKER_SELECTORS[i]);
      for (var j = 0; j < nodes.length; j++) {
        // El drawer del menu solo cuenta si de verdad entro a pantalla.
        if (BLOCKER_SELECTORS[i] === '.menu-drawer' && nodes[j].getBoundingClientRect().left < -50) continue;
        if (isVisible(nodes[j])) { blockers.push(BLOCKER_SELECTORS[i]); break; }
      }
    }
    // Chatwoot abierto = holder sin .woot--hide
    var woot = doc.querySelector('.woot-widget-holder');
    if (woot && !woot.classList.contains('woot--hide') && isVisible(woot)) blockers.push('chatwoot');

    // Overlays fixed que tapan el viewport pero son INVISIBLES (sin contenido ni fondo).
    var orphans = [];
    // El barrido es caro (getComputedStyle sobre TODOS los div/section/aside cada 800ms y
    // tras cada tap); solo tiene sentido si se van a neutralizar overlays.
    var all = NEUTRALIZE_ORPHAN_OVERLAYS ? doc.querySelectorAll('div,section,aside') : [];
    for (var k = 0; k < all.length; k++) {
      var el = all[k], cs = global.getComputedStyle(el);
      if (cs.position !== 'fixed' || cs.pointerEvents === 'none') continue;
      if (cs.display === 'none' || cs.visibility === 'hidden') continue;
      var r = el.getBoundingClientRect();
      if (r.width < global.innerWidth * 0.9 || r.height < global.innerHeight * 0.9 || r.top > 2) continue;
      var transparent = parseFloat(cs.opacity) === 0 ||
        (/rgba\(.*,\s*0\)$/.test(cs.backgroundColor) || cs.backgroundColor === 'transparent');
      var sinContenido = !(el.innerText || '').trim() && !el.querySelector('img,svg,video,input,button');
      if (transparent && sinContenido) orphans.push(el);
    }

    return {
      state: {
        bodyLockClasses: BODY_LOCK_CLASSES.filter(function (c) { return body.classList.contains(c); }),
        htmlLockClasses: HTML_LOCK_CLASSES.filter(function (c) { return html.classList.contains(c); }),
        bodyInlineOverflow: body.style.overflow || '',
        bodyInlinePosition: body.style.position || '',
        visibleBlockers: blockers,
        detailsOpen: detOpen,
        detailsMenuOpening: menuOpening,
        detailsOpenForMs: detailsOpenSince ? (Date.now() - detailsOpenSince) : 0,
        orphanOverlays: orphans.map(function (_, idx) { return idx; })
      },
      orphanNodes: orphans,
      details: det
    };
  }

  function tick() {
    var snap;
    try { snap = collect(); } catch (e) { return; }
    var d = decide(snap.state);
    if (!d.reasons.length) return;

    try {
      if (d.closeDetails && snap.details) {
        snap.details.open = false;
        snap.details.classList.remove('menu-opening');
        detailsOpenSince = 0;
      }
      if (d.clearBodyLocks) BODY_LOCK_CLASSES.forEach(function (c) { doc.body.classList.remove(c); });
      if (d.clearHtmlLocks) HTML_LOCK_CLASSES.forEach(function (c) { doc.documentElement.classList.remove(c); });
      if (d.clearInlineStyles) {
        if (doc.body.style.overflow === 'hidden') doc.body.style.removeProperty('overflow');
        if (doc.body.style.position === 'fixed') doc.body.style.removeProperty('position');
      }
      d.neutralize.forEach(function (idx) {
        var n = snap.orphanNodes[idx];
        if (n) n.style.setProperty('pointer-events', 'none', 'important');
      });
    } catch (e) { /* nunca romper la pagina por el rescate */ }
  }

  function start() {
    tick();
    global.setInterval(tick, 800);
    ['click', 'touchend', 'keyup', 'visibilitychange', 'popstate', 'rebuy:cart.change']
      .forEach(function (ev) { doc.addEventListener(ev, function () { global.setTimeout(tick, 60); }, true); });
  }

  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', start);
  else start();
})(typeof globalThis !== 'undefined' ? globalThis : this);
